import {
  CreateWorkspaceForm,
  UpdateWorkspaceBody
} from '@shared/dto/workspaces.dto'
import { Workspace } from '@shared/types/workspaces.types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import axiosClient from '../useAxios'

export const useCreateWorkspaceMutation = (companyId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['workspaces', 'create'],
    mutationFn: async (data: CreateWorkspaceForm): Promise<Workspace> => {
      const response = await axiosClient.post(
        `/companies/${companyId}/workspaces`,
        data
      )
      return response.data.data.workspace
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'workspaces']
      })
    }
  })
}

export const useDeleteWorkspaceMutation = (
  companyId: string,
  workspaceId: string
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['workspaces', 'delete'],
    mutationFn: async (): Promise<void> => {
      await axiosClient.delete(
        `/companies/${companyId}/workspaces/${workspaceId}`
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'workspaces']
      })
    }
  })
}

export const useUpdateWorkspaceMutation = (
  companyId: string,
  workspaceId: string
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['workspaces', 'update'],
    mutationFn: async (data: UpdateWorkspaceBody): Promise<Workspace> => {
      const response = await axiosClient.patch(
        `/companies/${companyId}/workspaces/${workspaceId}`,
        data
      )
      return response.data.data.workspace
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'workspaces']
      })
      await queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'workspaces', workspaceId]
      })
    }
  })
}

export const useDeleteWorkspaceUserMutation = (
  companyId: string,
  workspaceId: string
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['workspaces', 'user', 'delete'],
    mutationFn: async ({
      companyId,
      workspaceId,
      userId
    }: {
      companyId: string
      workspaceId: string
      userId: string
    }): Promise<void> => {
      await axiosClient.delete(
        `/companies/${companyId}/workspaces/${workspaceId}/users/${userId}`
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'workspaces']
      })
      await queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'workspaces', workspaceId, 'users']
      })
    }
  })
}
